// This file is auto-generated by @hey-api/openapi-ts

import { z } from "zod/v3";

/**
 * ConvergencePoint
 */
export const zConvergencePoint = z.object({
  t_s: z.number(),
  sheets_used: z.number().int(),
  consumed_utilization: z.number(),
});

/**
 * ItemCost
 */
export const zItemCost = z.object({
  item_id: z.string(),
  quantity: z.number().int(),
  net_area_mm2: z.number(),
  bbox_area_mm2: z.number(),
  allocated_area_mm2: z.number(),
});

/**
 * ItemSource
 */
export const zItemSource = z.object({
  kind: z.enum(["dxf", "json", "occt"]).optional().default("json"),
  ref: z.string().nullish().default(null),
  warnings: z.array(z.string()).optional().default([]),
});

/**
 * LayoutMetadata
 */
export const zLayoutMetadata = z.object({
  index: z.number().int(),
  count: z.number().int(),
  sheet_indices: z.array(z.number().int()),
  sheet_type_id: z.string(),
  width_mm: z.number(),
  height_mm: z.number(),
  used_length_mm: z.number(),
  utilization: z.number(),
  piece_count: z.number().int(),
  pieces: z.record(z.number().int()),
  svg_key: z.string().nullish().default(null),
});

/**
 * Metrics
 */
export const zMetrics = z.object({
  sheets_used: z.number().int(),
  item_area_mm2: z.number(),
  sheet_area_mm2: z.number(),
  consumed_area_mm2: z.number(),
  utilization: z.number(),
  consumed_utilization: z.number(),
  elapsed_s: z.number(),
  time_to_first_s: z.number().nullish().default(null),
  convergence: z.array(zConvergencePoint).optional().default([]),
});

/**
 * Placement
 */
export const zPlacement = z.object({
  item_id: z.string(),
  x: z.number(),
  y: z.number(),
  angle: z.number().optional().default(0),
  mirror: z.boolean().optional().default(false),
  matrix: z.tuple([z.number(), z.number(), z.number(), z.number(), z.number(), z.number()]),
});

/**
 * Point
 */
export const zPoint = z.object({
  x: z.number(),
  y: z.number(),
});

/**
 * Arc
 */
export const zArc = z.object({
  type: z.literal("arc").optional().default("arc"),
  start: zPoint,
  end: zPoint,
  center: zPoint,
  orientation: z.enum(["ccw", "cw"]).optional().default("ccw"),
});

/**
 * Line
 */
export const zLine = z.object({
  type: z.literal("line").optional().default("line"),
  start: zPoint,
  end: zPoint,
});

/**
 * Shape
 */
export const zShape = z.object({
  outer: z
    .array(
      z.union([
        z
          .object({
            type: z.literal("line"),
          })
          .and(zLine),
        z
          .object({
            type: z.literal("arc"),
          })
          .and(zArc),
      ]),
    )
    .min(1),
  holes: z
    .array(
      z.array(
        z.union([
          z
            .object({
              type: z.literal("line"),
            })
            .and(zLine),
          z
            .object({
              type: z.literal("arc"),
            })
            .and(zArc),
        ]),
      ),
    )
    .optional()
    .default([]),
});

/**
 * ItemType
 */
export const zItemType = z.object({
  id: z.string(),
  name: z.string().nullish().default(null),
  quantity: z.number().int().gte(1).optional().default(1),
  shape: zShape,
  allowed_rotations: z.array(z.number()).optional().default([0, 90, 180, 270]),
  allow_mirror: z.boolean().optional().default(false),
  source: zItemSource.optional().default({
    kind: "json",
    ref: null,
    warnings: [],
  }),
});

/**
 * SheetLayout
 */
export const zSheetLayout = z.object({
  sheet_type_id: z.string(),
  index: z.number().int(),
  width: z.number(),
  height: z.number(),
  placements: z.array(zPlacement),
  used_length: z.number(),
  utilization: z.number(),
});

/**
 * NestingSolution
 */
export const zNestingSolution = z.object({
  engine: z.string(),
  status: z.enum(["solved", "fallback"]),
  instance_hash: z.string(),
  sheets: z.array(zSheetLayout),
  metrics: zMetrics,
  items: z.array(zItemCost),
  warnings: z.array(z.string()).optional().default([]),
});

/**
 * NestingJobResult
 */
export const zNestingJobResult = z.object({
  nestingRunId: z.string(),
  companyId: z.string(),
  shouldApplyOnCompletion: z.boolean().optional().default(false),
  status: z.enum(["PROCESSED", "ERROR"]),
  solution: zNestingSolution.nullish().default(null),
  layouts: z.array(zLayoutMetadata).optional().default([]),
  sheetCapacity: z.number().int().nullish().default(null),
  error: z.string().nullish().default(null),
  errorCode: z.string().nullish().default(null),
  errorItemId: z.string().nullish().default(null),
});

/**
 * SheetType
 */
export const zSheetType = z.object({
  id: z.string(),
  width: z.number().gt(0),
  height: z.number().gt(0),
  quantity: z.number().int().gte(1).optional().default(100),
  cost: z.number().nullish().default(null),
});

/**
 * SolveOptions
 */
export const zSolveOptions = z.object({
  time_limit_s: z.number().gt(0).optional().default(5),
  mode: z.enum(["quick", "optimized"]).optional().default("quick"),
  deterministic: z.boolean().optional().default(false),
  deflection_mm: z.number().gt(0).optional().default(0.05),
});

/**
 * Spacing
 */
export const zSpacing = z.object({
  item_item_mm: z.number().gte(0).optional().default(0),
  item_sheet_mm: z.number().gte(0).optional().default(0),
});

/**
 * NestingInstance
 */
export const zNestingInstance = z.object({
  version: z.literal("1").optional().default("1"),
  units: z.literal("mm").optional().default("mm"),
  items: z.array(zItemType).min(1),
  sheets: z.array(zSheetType).min(1),
  spacing: zSpacing.optional().default({ item_item_mm: 0, item_sheet_mm: 0 }),
  objective: z
    .enum(["bin-packing", "bin-packing-with-leftovers", "open-dimension-x", "knapsack"])
    .optional()
    .default("bin-packing-with-leftovers"),
  solve: zSolveOptions.optional().default({
    time_limit_s: 5,
    mode: "quick",
    deterministic: false,
    deflection_mm: 0.05,
  }),
});

/**
 * NestingJob
 */
export const zNestingJob = z.object({
  nestingRunId: z.string(),
  companyId: z.string(),
  instance: zNestingInstance,
  shouldApplyOnCompletion: z.boolean().optional().default(false),
});
